import { defineStore } from 'pinia';
import { reactive, computed } from 'vue';
import Bugsnag from '@bugsnag/js';
import { createFilterEngine } from '@/engine/filter-engine.js';
import { removeFilterSelection } from '@/engine/filter-patch.js';
import { formatKey, monthKey } from '@/constants/lookup-keys.js';
import { generateTermStartPreselect } from '@/helpers/Date.js';
import { hasQueryParamOrNull, getQueryStringParameters, findCurrentNavCategory } from '@/helpers/QueryString.js';

/**
 * @typedef {import('@/types').FilterOptions} FilterOptions
 * @typedef {import('@/types').FilterSelected} FilterSelected
 * @typedef {import('@/types').FilterOption} FilterOption
 * @typedef {import('@/types').GroupedSelected} GroupedSelected
 * @typedef {import('@/types').NavCategory} NavCategory
 */

/**
 * @param {string | null} scopedCollege
 * @returns {FilterOptions}
 */
function buildDefaultOptions(scopedCollege) {
	/** @type {FilterOption[]} */
	let base = [
		{ text: 'Term', value: 'term' },
		{ text: 'Starts In', value: 'startsOn' },
		{ text: 'Campus', value: 'campus' },
		{ text: 'Display', value: 'enrollable' },
		{ text: 'Format', value: 'format' },
		{ text: 'Days', value: 'days' },
		{ text: 'Subject', value: 'subject' },
		{ text: 'Course', value: 'course' },
	];

	if (scopedCollege === null) {
		base.unshift({ text: 'College', value: 'college' });
	}

	return reactive({
		base,
		college: [
			{ text: 'All Colleges', value: '', type: '*' },
			{ text: 'Ashland', value: 'ashland' },
			{ text: 'Big Sandy', value: 'bigsandy' },
			{ text: 'Bluegrass', value: 'bluegrass' },
			{ text: 'Elizabethtown', value: 'elizabethtown' },
			{ text: 'Gateway', value: 'gateway' },
			{ text: 'Hazard', value: 'hazard' },
			{ text: 'Henderson', value: 'henderson' },
			{ text: 'Hopkinsville', value: 'hopkinsville' },
			{ text: 'Jefferson', value: 'jefferson' },
			{ text: 'Madisonville', value: 'madisonville' },
			{ text: 'Maysville', value: 'maysville' },
			{ text: 'Owensboro', value: 'owensboro' },
			{ text: 'Somerset', value: 'somerset' },
			{ text: 'Southcentral', value: 'southcentral' },
			{ text: 'Southeast', value: 'southeast' },
			{ text: 'West Kentucky', value: 'westkentucky' },
		],
		term: [],
		startsOn: {
			0: [{ value: '', text: 'All Months', type: '*' }],
		},
		campus: [{ value: '', text: 'All Campuses', type: '*' }],
		enrollable: [
			{ value: false, text: 'All Classes', type: '*' },
			{ value: true, text: 'Only Open Classes' },
		],
		format: [],
		days: [
			{ value: '', text: 'All Days', type: '*' },
			{ value: 'Mon', text: 'Monday' },
			{ value: 'Tues', text: 'Tuesday' },
			{ value: 'Wed', text: 'Wednesday' },
			{ value: 'Thu', text: 'Thursday' },
			{ value: 'Fri', text: 'Friday' },
			{ value: 'Sat', text: 'Saturday' },
			{ value: 'Sun', text: 'Sunday' },
		],
		subject: [],
		course: {
			0: [{ value: '', text: 'All Courses', type: '*' }],
		},
		loading: {
			college: false,
			term: true,
			startsOn: true,
			campus: true,
			enrollable: false,
			format: true,
			days: false,
			subject: true,
			course: false,
		},
	});
}

/**
 * @param {FilterOptions} options
 * @param {string | null} scopedCollege
 * @returns {FilterSelected}
 */
function buildDefaultSelected(options, scopedCollege) {
	return reactive({
		base: options.base.map((option) => {
			return option.hasOwnProperty('type') && (option.type === '*' || option.type === 'first');
		}),
		college: options.college.map((option) => {
			if (scopedCollege === null) {
				return option.hasOwnProperty('type') && (option.type === '*' || option.type === 'first');
			}
			return option.value === scopedCollege;
		}),
		term: [],
		startsOn: {
			0: options.startsOn['0'].map((option) => {
				return option.hasOwnProperty('type') && (option.type === '*' || option.type === 'first');
			}),
		},
		campus: [],
		enrollable: options.enrollable.map((option) => {
			return option.hasOwnProperty('type') && (option.type === '*' || option.type === 'first');
		}),
		format: [],
		days: options.days.map((option) => {
			return option.hasOwnProperty('type') && (option.type === '*' || option.type === 'first');
		}),
		subject: [],
		course: {
			0: options.course['0'].map((option) => {
				return option.hasOwnProperty('type') && (option.type === '*' || option.type === 'first');
			}),
		},
	});
}

export const useFilterSearchStore = defineStore('filter-search', () => {
	let scopedCollege = window.currentCollege !== undefined ? window.currentCollege : null;
	let queryParams = getQueryStringParameters();

	let preselectedFiltersConfig = {
		preselected: {
			term: {
				term_description: hasQueryParamOrNull('term_description', queryParams, generateTermStartPreselect()),
			},
		},
	};

	let options = buildDefaultOptions(scopedCollege);
	let selected = buildDefaultSelected(options, scopedCollege);

	let searchData = reactive({
		results: {},
		classModel: {},
		resultsLoading: true,
		navLoading: true,
		/** @type {NavCategory[]} */
		categories: [],
		currentNav: {
			name: 'Loading',
			count: 0,
			page: 1,
			initial: true,
		},
		currentNavResults: {
			name: 'Loading',
			count: 0,
			page: 1,
			initial: true,
		},
	});

	let showCollegeFilter = computed(() => scopedCollege === null);

	let filtersSelected = computed(() => {
		return Object.entries(selected).filter((option) => option[0] !== 'term' && option[0] !== 'base').map((option) => {
			if (Array.isArray(option[1])) {
				let optionList = /** @type {FilterOption[]} */ (options[option[0]]);
				return option[1].map((value, index) => {
					return {
						type: option[0],
						typeText: option[0] === 'enrollable' ? 'display' : option[0],
						value: optionList[index].value,
						text: optionList[index].text,
						selected: value,
						index,
					};
				});
			}

			let groupedOptions = /** @type {Record<string, FilterOption[]>} */ (options[option[0]]);
			return Object.entries(/** @type {GroupedSelected} */ (option[1])).map((courseSubject) => {
				return courseSubject[1].map((value, index) => {
					return {
						type: option[0],
						typeText: option[0] === 'startsOn' ? 'starts In' : option[0],
						key: courseSubject[0],
						value: groupedOptions[courseSubject[0]][index].value,
						text: groupedOptions[courseSubject[0]][index].text,
						selected: value,
						index,
					};
				});
			}).flat().filter((chip) => chip.selected && chip.type !== '0');
		}).flat().filter((chip) => {
			if (!Array.isArray(options[chip.type]) && 'key' in chip && typeof chip.key === 'string') {
				let groupedOptions = /** @type {Record<string, FilterOption[]>} */ (options[chip.type]);
				return chip.selected && groupedOptions[chip.key][chip.index].type !== '*';
			}

			let optionList = /** @type {FilterOption[]} */ (options[chip.type]);
			return chip.selected && optionList[chip.index].type !== '*';
		});
	});

	let filterEngine = createFilterEngine({
		liveState: {
			options,
			selected,
			data: searchData,
		},
		context: {
			formatKey,
			monthKey,
			preselectedFiltersConfig,
			lockCollegeFilter: scopedCollege !== null,
			onError: (err) => Bugsnag.notify(/** @type {Error} */ (err)),
		},
	});

	/**
	 * @param {{ source: string, index: number, key?: string | null, value: boolean }} patch
	 */
	function patchFilterSelection({ source, index, key, value }) {
		if (key !== null && key !== undefined) {
			/** @type {GroupedSelected} */ (selected[source])[key][index] = value;
		} else {
			/** @type {boolean[]} */ (selected[source])[index] = value;
		}
	}

	/**
	 * @param {{ source: string, index: number, key?: string | null }} selection
	 */
	function removeFilter({ source, index, key }) {
		removeFilterSelection(options, selected, { source, index, key });
		return filterEngine.dispatch({ type: 'REMOVE_FILTER', source });
	}

	function removeAllFilters() {
		return filterEngine.dispatch({ type: 'REMOVE_ALL_FILTERS' });
	}

	/**
	 * @param {string} source
	 */
	function commitFilter(source) {
		return filterEngine.dispatch({ type: 'COMMIT_FILTER', source });
	}

	/**
	 * @param {number} page
	 */
	function paginate(page) {
		return filterEngine.dispatch({
			type: 'PAGINATE',
			runtime: {
				page,
				category: findCurrentNavCategory(searchData.categories, page),
			},
		});
	}

	/**
	 * @param {NavCategory} category
	 */
	function navigateCategory(category) {
		return filterEngine.dispatch({
			type: 'CATEGORY_NAV',
			runtime: {
				page: category.page,
				category,
			},
		});
	}

	/**
	 * @param {Record<string, unknown>} classModel
	 */
	function setClassModel(classModel) {
		searchData.classModel = classModel;
	}

	filterEngine.dispatch({ type: 'INIT' });

	return {
		showCollegeFilter,
		options,
		selected,
		searchData,
		filtersSelected,
		patchFilterSelection,
		removeFilter,
		removeAllFilters,
		commitFilter,
		paginate,
		navigateCategory,
		setClassModel,
	};
});
